import JSONbigint from "json-bigint";
import rest, { api, specUrl } from "./api";
import type { RequestBody, Result } from "./spec";
import { Account, AnonymousAccount } from "./types";
import { setItem, getItem, removeItem } from "./storage";

export const authTokenKey = "jwt_token";
export const refreshTokenKey = "refresh_token";
export const authAnoTokenKey = "ano_jwt_token";
export const alien = "alien";
export const redirectUrl = "redirect_url";
export const lastMessageIdKey = "lastMessageID";

export interface AuthResponse {
  jwtToken?: string;
  token?: string;
  refreshToken: string;
  account?: Account;
  /** Refresh only: the Device the request named is not the caller's (removed, evicted, expired). */
  deviceUnknown?: boolean;
}

// ─── Request shapes ───────────────────────────────────────────────────────────
// Request bodies are generated from the real Go request structs, so they are
// the trustworthy half of the spec and are adopted directly. (Several *response*
// types are hand-maintained doc structs in fiapi/swagger_responses.go and are
// kept hand-typed below where they disagree with the handlers.)

export type SignUpEmailReq = RequestBody<"account/signup/email", "post">;
export type LogoutReq = RequestBody<"account/logout", "post">;
export type AccountFilter = RequestBody<"account/get", "post">;
export type ReportAccountReq = RequestBody<"account/report", "post">;
export type BlockAccountReq = RequestBody<"account/block", "post">;
export type BlockList = Result<"account/block", "get">;
export type BlockEntry = BlockList["matching"][number];
export type ResetPasswordReq = RequestBody<"account/reset/password", "post">;
export type SendOtpReq = RequestBody<"account/otp", "post">;
export type LoginReq = RequestBody<"account/login", "post">;

// The two update requests are the spec types, not widened ones: stringr ignores any
// field it does not declare, so an extra property compiles, "succeeds" and changes
// nothing. `request-contract.test.ts` fails if a request type here grows one.
export type UpdateAccountReq = RequestBody<"account/update", "post">;
export type AnoUpdateAccountReq = RequestBody<"anonymous/update", "post">;

export interface Alien {
  id: string;
  createdAt?: string;
  username: string;
  imageURL?: string;
  bio?: string;
}

const Service = {
  // NOTE: login/signup/otp/refresh responses are documented by hand-written
  // structs in fiapi/swagger_responses.go that do not match the handlers
  // (e.g. swaggerOtpRes says `id: string`, but ResetPasswordReq.OtpId is
  // uint64). Those keep their hand-written response types; only the URL and
  // request body are spec-checked.
  login: (body: LoginReq) =>
    rest.post<AuthResponse>(specUrl("account/login"), { body }),

  signup: (body: SignUpEmailReq) =>
    rest.post<AuthResponse>(specUrl("account/signup"), { body }),

  signupEmail: (body: SignUpEmailReq) =>
    rest.post<AuthResponse>(specUrl("account/signup/email"), { body }),

  // No request body or 200 schema is declared for the phone endpoints.
  signupPhone: (phone: string) =>
    rest.post<{ sessionId: string; displayInstruction: string; smsUri: string }>(
      specUrl("account/signup/phone"), { body: { phone } }),

  signupPhoneConfirm: (sessionId: string) =>
    rest.post<{ status?: string; account?: Account; token?: string; refreshToken?: string }>(
      specUrl("account/signup/phone/confirm"), { body: { sessionId } }),

  sendOtp: (body: SendOtpReq) =>
    rest.post<{ id: number }>(specUrl("account/otp"), { body }),

  resetPassword: (body: ResetPasswordReq) =>
    api.post("account/reset/password", { body }),

  // Still the deprecated GET, as mobile uses: the Go backend still deployed on
  // fsn1 has no POST refresh, and a 404 there would read as a transient failure
  // that never signs out. Move to POST {accountId, refresh} once fsn1 is gone.
  // Naming a Device is the exception: only a browser that registered one with
  // stringr has an id, and the GET cannot carry it.
  refreshToken: (accountId: string, refresh: string, timeout?: number, deviceId?: string) =>
    deviceId
      ? rest.post<AuthResponse>(specUrl("account/auth/refresh"), {
          body: { accountId, refresh, deviceId } satisfies RequestBody<"account/auth/refresh", "post">,
          timeout,
        })
      : rest.get<AuthResponse>(specUrl("account/auth/refresh"), {
          params: { accountId, refresh },
          timeout,
        }),

  /**
   * Revokes the session server-side. With `token`, that bearer is sent as given
   * and nothing in storage is read: sign-out wipes storage before this call goes
   * out, and a refused token must not start a second sign-out.
   */
  logout: (body: LogoutReq, token?: string, timeout?: number) =>
    api.post(
      "account/logout",
      token ? { body, headers: { Authorization: `Bearer ${token}` }, timeout } : { body, hasAuth: true, timeout }
    ),

  getAccount: () => api.get("account/", { hasAuth: true }),

  getPublicAccount: (accountId: string) =>
    api.get("account/public/{accountId}", { path: { accountId } }),

  getAccountById: (accountId: string) =>
    api.get("account/{accountId}", { path: { accountId }, hasAuth: true }),

  getMultipleAccounts: (body: AccountFilter) =>
    api.post("account/get", { body, hasAuth: true }),

  // `rest` rather than `api` only because the response is the app-level Account.
  updateAccount: (body: UpdateAccountReq): Promise<Account> =>
    rest.post<Account>(specUrl("account/update"), { body, hasAuth: true }),

  updateAnonymousAccount: (body: AnoUpdateAccountReq): Promise<AnonymousAccount> =>
    rest.post<AnonymousAccount>(specUrl("anonymous/update"), { body, hasAuth: true }),

  deleteAccount: (body: RequestBody<"account/delete", "post">) =>
    api.post("account/delete", { body, hasAuth: true }),

  blockAccount: (body: BlockAccountReq) =>
    api.post("account/block", { body, hasAuth: true }),

  unblockAccount: (accountId: string) =>
    api.del("account/block", { hasAuth: true, params: { accountId } }),

  listBlocks: () => api.get("account/block", { hasAuth: true }),

  reportAccount: (body: ReportAccountReq) =>
    api.post("account/report", { body, hasAuth: true }),

  checkUsernameAvailability: (username: string) =>
    api.get("account/username/available", { hasAuth: true, params: { username } }),

  getAppVersion: () => api.get("appversion", {}),

  // ─── Passkey ────────────────────────────────────────────────────────────────
  // WebAuthn credential payloads are opaque browser objects; the spec models
  // them loosely, so these keep hand-written shapes with a checked URL.

  beginPasskeyLogin: (identifier: string) =>
    rest.post<{ publicKey: unknown }>(specUrl("passkey/login/begin"), {
      body: { identifier },
    }),

  completePasskeyLogin: (body: Record<string, unknown>) =>
    rest.post<AuthResponse>(specUrl("passkey/login/complete"), { body }),

  beginPasskeyRegistration: () =>
    rest.post<{ publicKey: unknown }>(specUrl("passkey/register/begin"), {
      hasAuth: true,
    }),

  completePasskeyRegistration: (body: Record<string, unknown>) =>
    rest.post<{ registered: boolean }>(specUrl("passkey/register/complete"), {
      hasAuth: true,
      body,
    }),

  beginPasskeyReset: (body: RequestBody<"passkey/reset/begin", "post">) =>
    rest.post<{ otpId?: number; sessionId?: string; displayInstruction?: string; smsUri?: string; registrationOptions: { publicKey: unknown }; hasHistoryBackup: boolean }>(
      specUrl("passkey/reset/begin"), { body }),

  completePasskeyReset: (body: Record<string, unknown>) =>
    rest.post<{ registered?: boolean; status?: string }>(
      specUrl("passkey/reset/complete"), { body }),

  listPasskeys: () => api.get("passkey/", { hasAuth: true }),

  deletePasskey: (credentialId: string) =>
    api.del("passkey/{credentialId}", { path: { credentialId }, hasAuth: true }),

  // ─── Deprecated aliases ─────────────────────────────────────────────────────

  /** @deprecated use login */
  loginByEmail: (body: LoginReq) =>
    rest.post<AuthResponse>(specUrl("account/login"), { body }),

  /** @deprecated use sendOtp */
  getOtp: (body: SendOtpReq) =>
    rest.post<{ id: number }>(specUrl("account/otp"), { body }),

  /** @deprecated use signupEmail */
  signUpEmail: (body: SignUpEmailReq) =>
    rest.post<AuthResponse>(specUrl("account/signup/email"), { body }),

  /** @deprecated use updateAccount */
  update: (body: UpdateAccountReq): Promise<Account> =>
    rest.post<Account>(specUrl("account/update"), { body, hasAuth: true }),

  /** @deprecated use getPublicAccount */
  getAccountByIdPublic: (accountId: string) =>
    api.get("account/public/{accountId}", { path: { accountId } }),

  /** @deprecated use getAccountById */
  lookupAccount: (accountId: string) =>
    api.get("account/{accountId}", { path: { accountId }, hasAuth: true }),

  /** @deprecated use getMultipleAccounts */
  getMultipleAccount: (body: AccountFilter) =>
    api.post("account/get", { body, hasAuth: true }),

  // ─── Token storage helpers ───────────────────────────────────────────────────

  getToken: () => getItem(authTokenKey),
  getRefreshToken: () => getItem(refreshTokenKey),
  getAnoToken: () => getItem(authAnoTokenKey),
  getRedirectUrl: () => getItem(redirectUrl),
  getAlien: (): Account | null => {
    const alienInfo = getItem(alien);
    return alienInfo ? JSONbigint.parse(alienInfo) : null;
  },
  setAlien: (alienInfo: Alien) => {
    setItem(alien, JSONbigint.stringify(alienInfo));
  },
  setToken: (token: string) => {
    setItem(authTokenKey, token);
  },
  setRefreshToken: (token: string) => setItem(refreshTokenKey, token),
  setAnoToken: (token: string) => setItem(authAnoTokenKey, token),
  setRedirectUrl: (url: string) => setItem(redirectUrl, url),
  removeRedirectUrl: () => removeItem(redirectUrl),
  remToken: () => {
    removeItem(authTokenKey);
  },
  remAlien: () => {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(authAnoTokenKey);
    localStorage.removeItem(alien);
  },
};

export default Service;

// ─── Backwards-compat type re-exports ────────────────────────────────────────
/** @deprecated use inline type { username?: string; email?: string; password: string } */
export interface LoginByEmailAndPasswordParameter {
  username?: string;
  email?: string;
  password: string;
  anonymous?: boolean;
}
/** @deprecated use { id: number } */
export type GetOtpRes = { id: number }
/** @deprecated use SignUpEmailReq */
export type SignUpByEmailParams = SignUpEmailReq
/** @deprecated use AccountFilter */
export type GetMultipleAccountParamater = AccountFilter
